import { Scroller } from '@theme/scrolling';
import { KbImageSource } from '@theme/kb-image-source';
import { kbImagePreload } from '@theme/kb-image-preload';
import { crossfadeMedia } from '@theme/kb-view-transition';

/**
 * @typedef {object} ApplyFinishOptions
 * @property {boolean} [silent]
 */

/**
 * @typedef {object} ActivateOptions
 * @property {boolean} [animate]
 */

/**
 * @typedef {object} KbMediaVariant
 * @property {string} [type]
 * @property {import('@theme/kb-image-source').KbImageSourceDescriptor|null} [source]
 * @property {string|null} [poster]
 * @property {Array<{ url: string, type: string }>} [sources]
 */

/**
 * @typedef {object} KbShadeMedia
 * @property {KbMediaVariant|null} [desktop]
 * @property {KbMediaVariant|null} [mobile]
 * @property {string} [alt]
 * @property {string|null} [viewport]
 */

class KbPdpGallery extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      viewport: '[data-kb-gallery-viewport]',
      track: '[data-kb-gallery-track]',
      slide: '[data-kb-gallery-slide]',
      shadeSlide: '[data-kb-gallery-shade-slide]',
      controls: '[data-kb-gallery-controls]',
      playbar: '[data-kb-gallery-playbar]',
      segments: '[data-kb-gallery-segments]',
      zoom: '[data-kb-gallery-zoom]',
      video: '[data-kb-gallery-video]',
      media: 'img, video',
      videoGlyphTemplate: '[data-kb-gallery-video-glyph]',
      videoToggleTemplate: '[data-kb-gallery-video-toggle]',
    };
    this.classNames = {
      slide: 'kb-pdp-gallery__slide',
      image: 'kb-pdp-gallery__image',
      video: 'kb-pdp-gallery__video',
      videoToggle: 'kb-pdp-gallery__video-toggle',
      segment: 'kb-pdp-gallery__segment',
      segmentLine: 'kb-pdp-gallery__segment-line',
      segmentFill: 'kb-pdp-gallery__segment-fill',
      segmentActive: 'is-active',
      videoIndicator: 'kb-pdp-gallery__video-indicator',
      slideVideoPaused: 'is-video-paused',
    };
    this.dataAttributes = {
      slide: 'data-kb-gallery-slide',
      shadeSlide: 'data-kb-gallery-shade-slide',
      video: 'data-kb-gallery-video',
    };
    this.eventNames = {
      variantChange: 'kb:variant:change',
      lightboxOpen: 'kb:lightbox:open',
    };
    this.fillAnimationName = 'kb-gallery-fill';
    this.settings = {
      imageDurationMs: 5000,
      autoplay: true,
      videoAutoplay: true,
      hostSelector: 'kb-pdp-product',
      desktopMediaQuery: '(min-width: 990px)',
      activeThreshold: 0.5,
      dragThreshold: 10,
      glyphMinProgress: 0.3,
      programmaticScrollMs: 700,
      preloadAheadCount: 2,
    };
    this.imageSource = new KbImageSource();
    /**
     * @type {{
     *   index: number,
     *   playing: boolean,
     *   mode: string,
     *   finish: string|null,
     *   shadeName: string|null,
     *   cartridgeProductId: number|string|null,
     *   desktop: boolean,
     *   desktopShadeActive: boolean,
     *   reducedMotion: boolean,
     *   hovered: boolean,
     *   focused: boolean,
     *   userPausedVideo: boolean
     * }}
     */
    this.state = {
      index: 0,
      playing: false,
      mode: 'finish',
      finish: null,
      shadeName: null,
      cartridgeProductId: null,
      desktop: false,
      desktopShadeActive: false,
      reducedMotion: false,
      hovered: false,
      focused: false,
      userPausedVideo: false,
    };
    /** @type {Element|null} */
    this.host = null;
    /** @type {HTMLElement|null} */
    this.track = null;
    /** @type {HTMLElement|null} */
    this.segmentsContainer = null;
    /** @type {HTMLElement[]} */
    this.serverSlides = [];
    /** @type {HTMLElement[]} */
    this.visibleSlides = [];
    /** @type {Array<HTMLElement|null>} */
    this.segments = [];
    /** @type {MediaQueryList|null} */
    this.mediaQuery = null;
    /** @type {IntersectionObserver|null} */
    this.slideObserver = null;
    /** @type {Scroller|null} */
    this.scroller = null;
    this.dragging = false;
    this.suppressClick = false;
    /** @type {Map<number, HTMLElement>} */
    this.glyphByIndex = new Map();
    /** @type {number|null} */
    this.programmaticIndex = null;
    this.scrollSettleTimer = 0;
    this.abortController = new AbortController();
    this.slideSetAbortController = new AbortController();
  }

  connectedCallback() {
    this.track = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.track));
    if (!this.track) return;
    this.serverSlides = this.slideElements();
    if (this.serverSlides.length === 0) return;

    this.host = this.closest(this.settings.hostSelector);
    this.segmentsContainer = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.segments));
    this.state.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.mediaQuery = window.matchMedia(this.settings.desktopMediaQuery);
    this.state.desktop = this.mediaQuery.matches;

    const seconds = Number(this.dataset.autoplaySeconds);
    if (!Number.isNaN(seconds) && seconds > 0) this.settings.imageDurationMs = seconds * 1000;
    this.settings.autoplay = this.dataset.autoplay !== 'false';
    this.settings.videoAutoplay = this.dataset.videoAutoplay !== 'false';

    this.bindControls();
    this.setupDrag();
    this.listenForVariantChange();
    this.listenForVisibility();
    this.applyFinish(this.hostFinish(), { silent: true });
    this.activate(0, { animate: false });
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.slideSetAbortController.abort();
    if (this.slideObserver) this.slideObserver.disconnect();
    if (this.scroller) this.scroller.destroy();
    if (this.scrollSettleTimer) clearTimeout(this.scrollSettleTimer);
  }

  /** @returns {HTMLElement[]} */
  slideElements() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll(this.selectors.slide)));
  }

  /** @returns {string|null} */
  hostFinish() {
    if (!this.host) return null;
    const selection = /** @type {{ finish?: string|null }|null} */ (/** @type {any} */ (this.host).selection);
    if (selection && typeof selection.finish === 'string') return selection.finish;
    return null;
  }

  /**
   * @param {string|null} finish
   * @param {ApplyFinishOptions} [options]
   */
  applyFinish(finish, options = {}) {
    this.removeShadeSlides();
    this.applyMediaFocus(null);
    this.state.mode = 'finish';
    this.state.finish = finish;
    const active = finish || '';
    const viewport = this.state.desktop ? 'desktop' : 'mobile';
    this.serverSlides.forEach((slide) => {
      const finishes = (slide.dataset.finish || '').trim().split(/\s+/).filter(Boolean);
      const viewports = (slide.dataset.viewport || '').trim().split(/\s+/).filter(Boolean);
      const finishMatches = finishes.length === 0 || active === '' || finishes.includes(active);
      const viewportMatches = viewports.length === 0 || viewports.includes(viewport);
      slide.hidden = !(finishMatches && viewportMatches);
    });
    this.visibleSlides = this.serverSlides.filter((slide) => !slide.hidden);
    this.commitSlideSet();
    if (!options.silent) this.activate(0, { animate: false });
  }

  /**
   * @param {string|null} shadeName
   * @param {number|string|null} cartridgeProductId
   */
  applyShade(shadeName, cartridgeProductId) {
    const entries = this.shadeGallery(shadeName, cartridgeProductId);
    if (entries.length === 0) {
      if (this.state.mode === 'finish') return;
      this.applyFinish(this.state.finish, { silent: false });
      return;
    }
    this.removeShadeSlides();
    this.applyMediaFocus(this.shadeCartridgeType(shadeName, cartridgeProductId));
    this.state.mode = 'shade';
    this.state.shadeName = shadeName;
    this.state.cartridgeProductId = cartridgeProductId;
    this.serverSlides.forEach((slide) => {
      slide.hidden = true;
    });
    const slides = entries.map((entry) => this.buildShadeSlide(entry, shadeName));
    if (this.track) slides.forEach((slide) => this.track?.appendChild(slide));
    this.visibleSlides = slides;
    this.commitSlideSet();
    this.activate(0, { animate: false });
  }

  /**
   * Frames media vertically for whatever is on screen. The host carries the map only
   * when the block setting is on, so with it off this is inert and the crop stays centred.
   * @param {string|null} cartridgeType
   */
  applyMediaFocus(cartridgeType) {
    const fallback = this.getAttribute('data-focus-default');
    if (fallback === null) return;
    const key = typeof cartridgeType === 'string' ? cartridgeType.trim().toLowerCase() : '';
    const mapped = key ? this.getAttribute(`data-focus-${key}`) : null;
    this.style.setProperty('--kb-media-focus-y', `${mapped ?? fallback}%`);
  }

  /** @returns {string} */
  mediaFocus() {
    if (!this.state.desktop) return '50%';
    return this.style.getPropertyValue('--kb-media-focus-y').trim() || '50%';
  }

  /**
   * @param {string|null} shadeName
   * @param {number|string|null} cartridgeProductId
   * @returns {string|null}
   */
  shadeCartridgeType(shadeName, cartridgeProductId) {
    const data = this.host ? /** @type {any} */ (this.host).data : null;
    if (!data || !shadeName) return null;
    const shade = this.findShade(data, cartridgeProductId, shadeName);
    if (!shade || typeof shade.cartridgeType !== 'string') return null;
    return shade.cartridgeType;
  }

  /**
   * @param {KbShadeMedia|null} entry
   * @returns {KbMediaVariant|null}
   */
  resolveMediaVariant(entry) {
    if (!entry) return null;
    const primary = this.state.desktop ? entry.desktop : entry.mobile;
    const fallback = this.state.desktop ? entry.mobile : entry.desktop;
    if (this.variantHasMedia(primary)) return primary || null;
    if (this.variantHasMedia(fallback)) return fallback || null;
    return null;
  }

  /**
   * @param {KbMediaVariant|null|undefined} variant
   * @returns {boolean}
   */
  variantHasMedia(variant) {
    if (!variant) return false;
    if (variant.source && variant.source.url) return true;
    return Boolean(variant.poster || (variant.sources && variant.sources.length));
  }

  /**
   * @param {string|null} shadeName
   * @param {number|string|null} cartridgeProductId
   * @returns {KbShadeMedia[]}
   */
  shadeGallery(shadeName, cartridgeProductId) {
    const data = this.host ? /** @type {any} */ (this.host).data : null;
    if (!data || !shadeName) return [];
    const shade = this.findShade(data, cartridgeProductId, shadeName);
    if (!shade) return [];
    // A position that exists in one list only belongs to that list's viewport. Without the
    // guard resolveMediaVariant falls back across viewports and hangs a phone crop on the
    // desktop hero, so the slide count stops matching the gallery field the merchant edits.
    const viewport = this.state.desktop ? 'desktop' : 'mobile';
    const gallery = Array.isArray(shade.gallery)
      ? shade.gallery.filter(
          (/** @type {KbShadeMedia} */ entry) =>
            (!entry.viewport || entry.viewport === viewport) && this.resolveMediaVariant(entry)
        )
      : [];
    if (gallery.length > 0) return gallery;
    if (shade.modelImage) {
      return [{ desktop: { type: 'image', source: shade.modelImage, poster: null, sources: [] }, mobile: null, alt: shadeName || '' }];
    }
    return [];
  }

  /**
   * @param {any} data
   * @param {number|string|null} cartridgeProductId
   * @param {string} shadeName
   * @returns {any}
   */
  findShade(data, cartridgeProductId, shadeName) {
    const cartridges = Array.isArray(data.cartridges) ? data.cartridges : [];
    const selectedCartridge = cartridges.find((/** @type {any} */ cartridge) =>
      this.matchesId(cartridge.id, cartridgeProductId)
    );
    /** @type {any[][]} */
    const pools = [];
    if (selectedCartridge && Array.isArray(selectedCartridge.shades)) pools.push(selectedCartridge.shades);
    cartridges.forEach((/** @type {any} */ cartridge) => {
      if (cartridge !== selectedCartridge && Array.isArray(cartridge.shades)) pools.push(cartridge.shades);
    });
    if (Array.isArray(data.shades)) pools.push(data.shades);
    for (const pool of pools) {
      const match = pool.find((/** @type {any} */ shade) => shade && shade.name === shadeName);
      if (match) return match;
    }
    return null;
  }

  /**
   * @param {KbShadeMedia} entry
   * @param {string|null} shadeName
   * @returns {HTMLElement}
   */
  buildShadeSlide(entry, shadeName) {
    const variant = this.resolveMediaVariant(entry) || { type: 'image', source: null, poster: null, sources: [] };
    const slide = document.createElement('div');
    slide.className = this.classNames.slide;
    slide.setAttribute(this.dataAttributes.slide, '');
    slide.setAttribute(this.dataAttributes.shadeSlide, '');
    const isVideo = variant.type === 'video';
    slide.dataset.mediaType = isVideo ? 'video' : 'image';
    if (variant.source && variant.source.masterWidth) {
      slide.dataset.masterWidth = String(variant.source.masterWidth);
    }
    if (isVideo) {
      const video = document.createElement('video');
      video.className = this.classNames.video;
      video.setAttribute(this.dataAttributes.video, '');
      video.muted = true;
      video.preload = 'metadata';
      video.setAttribute('playsinline', '');
      if (variant.poster) video.poster = variant.poster;
      (variant.sources || []).forEach((source) => {
        const element = document.createElement('source');
        element.src = source.url;
        if (source.type) element.type = source.type;
        video.appendChild(element);
      });
      slide.appendChild(video);
      const toggle = this.cloneVideoToggle();
      if (toggle) slide.appendChild(toggle);
    } else {
      const image = document.createElement('img');
      image.className = this.classNames.image;
      image.alt = this.shadeSlideAlt(entry, shadeName);
      image.loading = 'eager';
      this.imageSource.apply(image, variant.source);
      slide.appendChild(image);
    }
    return slide;
  }

  /**
   * @param {KbShadeMedia} entry
   * @param {string|null} shadeName
   * @returns {string}
   */
  shadeSlideAlt(entry, shadeName) {
    return entry.alt || shadeName || '';
  }

  /** @returns {HTMLElement|null} */
  cloneVideoToggle() {
    const template = this.querySelector(this.selectors.videoToggleTemplate);
    if (!(template instanceof HTMLTemplateElement)) return null;
    const node = template.content.firstElementChild;
    if (!node) return null;
    const clone = node.cloneNode(true);
    return clone instanceof HTMLElement ? clone : null;
  }

  removeShadeSlides() {
    this.querySelectorAll(this.selectors.shadeSlide).forEach((node) => node.remove());
  }

  commitSlideSet() {
    this.buildSegments();
    this.updateChromeVisibility();
    this.bindSlideVideos();
    this.observeVisibleSlides();
    this.recomputePlaying();
    this.queueAheadMedia();
    this.queuePosters();
  }

  queuePosters() {
    /** @type {import('@theme/kb-image-preload').KbPreloadEntry[]} */
    const entries = [];
    this.visibleSlides.forEach((slide, position) => {
      const video = slide.querySelector('video[data-poster]');
      if (!(video instanceof HTMLVideoElement)) return;
      if (position === 0) {
        this.applyPoster(video);
        return;
      }
      entries.push({ url: video.dataset.poster || '' });
    });
    kbImagePreload.add(entries);
  }

  /** @param {Element|null} video */
  applyPoster(video) {
    if (!(video instanceof HTMLVideoElement)) return;
    const poster = video.dataset.poster;
    if (!poster) return;
    video.poster = poster;
    delete video.dataset.poster;
  }

  queueAheadMedia() {
    const entries = this.visibleSlides
      .slice(1, 1 + this.settings.preloadAheadCount)
      .map((slide) => kbImagePreload.fromElement(slide));
    if (this.state.mode === 'finish') {
      const viewport = this.state.desktop ? 'desktop' : 'mobile';
      const queuedFinishes = new Set();
      this.serverSlides.forEach((slide) => {
        if (!slide.hidden) return;
        const finish = slide.dataset.finish || '';
        if (!finish || finish === (this.state.finish || '') || queuedFinishes.has(finish)) return;
        const viewports = (slide.dataset.viewport || '').trim().split(/\s+/).filter(Boolean);
        if (viewports.length > 0 && !viewports.includes(viewport)) return;
        queuedFinishes.add(finish);
        entries.push(kbImagePreload.fromElement(slide));
      });
    }
    kbImagePreload.add(entries);
  }

  recomputePlaying() {
    this.state.playing =
      this.settings.autoplay && !this.state.reducedMotion && this.visibleSlides.length > 1;
  }

  buildSegments() {
    if (!this.segmentsContainer) return;
    this.segmentsContainer.textContent = '';
    this.segments = [];
    this.glyphByIndex = new Map();
    this.visibleSlides.forEach((slide, index) => {
      if (slide.dataset.mediaType === 'video') {
        const glyph = this.createVideoGlyph();
        if (glyph) {
          this.segmentsContainer?.appendChild(glyph);
          this.glyphByIndex.set(index, glyph);
        }
        this.segments.push(null);
        return;
      }
      const segment = document.createElement('button');
      segment.type = 'button';
      segment.className = this.classNames.segment;
      segment.setAttribute('aria-label', this.segmentLabel(index, this.visibleSlides.length));
      const line = document.createElement('span');
      line.className = this.classNames.segmentLine;
      const fill = document.createElement('span');
      fill.className = this.classNames.segmentFill;
      line.appendChild(fill);
      segment.appendChild(line);
      this.segmentsContainer?.appendChild(segment);
      this.segments.push(segment);
    });
  }

  /**
   * @param {number} index
   * @param {number} total
   * @returns {string}
   */
  segmentLabel(index, total) {
    const template = this.segmentsContainer?.dataset.kbSegmentLabel || 'Slide %1 of %2';
    return template.replace('%1', String(index + 1)).replace('%2', String(total));
  }

  /** @returns {HTMLElement|null} */
  createVideoGlyph() {
    const template = this.querySelector(this.selectors.videoGlyphTemplate);
    if (!(template instanceof HTMLTemplateElement)) return null;
    const node = template.content.firstElementChild;
    if (!node) return null;
    const clone = node.cloneNode(true);
    return clone instanceof HTMLElement ? clone : null;
  }

  updateChromeVisibility() {
    const count = this.visibleSlides.length;
    const controls = this.querySelector(this.selectors.controls);
    if (controls instanceof HTMLElement) controls.hidden = count < 1;
    const playbar = this.querySelector(this.selectors.playbar);
    if (playbar instanceof HTMLElement) playbar.hidden = count <= 1;
  }

  bindControls() {
    const { signal } = this.abortController;
    const zoom = this.querySelector(this.selectors.zoom);

    if (zoom) zoom.addEventListener('click', this.handleZoom, { signal });
    if (this.segmentsContainer) {
      this.segmentsContainer.addEventListener('animationend', this.handleFillAnimationEnd, { signal });
      this.segmentsContainer.addEventListener('click', this.handleSegmentsClick, { signal });
    }

    this.addEventListener('mouseenter', this.handleMouseEnter, { signal });
    this.addEventListener('mouseleave', this.handleMouseLeave, { signal });
    this.addEventListener('focusin', this.handleFocusIn, { signal });
    this.addEventListener('focusout', this.handleFocusOut, { signal });
    if (this.mediaQuery) this.mediaQuery.addEventListener('change', this.handleBreakpointChange, { signal });
  }

  bindSlideVideos() {
    this.slideSetAbortController.abort();
    this.slideSetAbortController = new AbortController();
    const { signal } = this.slideSetAbortController;
    this.visibleSlides.forEach((slide) => {
      const video = this.videoOf(slide);
      if (!video) return;
      video.muted = true;
      video.addEventListener('timeupdate', this.handleVideoTimeUpdate, { signal });
      video.addEventListener('ended', this.handleVideoEnded, { signal });
      video.addEventListener('play', this.handleVideoPlayState, { signal });
      video.addEventListener('pause', this.handleVideoPlayState, { signal });
    });
  }

  observeVisibleSlides() {
    if (!this.track) return;
    if (this.slideObserver) {
      this.slideObserver.disconnect();
    } else {
      this.slideObserver = new IntersectionObserver(this.handleSlideIntersect, {
        root: this.track,
        threshold: [0.5, 0.75, 1],
      });
    }
    this.visibleSlides.forEach((slide) => this.slideObserver?.observe(slide));
  }

  setupDrag() {
    if (!this.track) return;
    this.scroller = new Scroller(this.track, { onScroll: () => {} });
    const { signal } = this.abortController;
    this.track.addEventListener('pointerdown', this.handlePointerDown, { signal });
    this.track.addEventListener('click', this.handleTrackClick, { signal });
  }

  listenForVariantChange() {
    const target = this.host || document;
    target.addEventListener(this.eventNames.variantChange, this.handleVariantChange, {
      signal: this.abortController.signal,
    });
  }

  listenForVisibility() {
    document.addEventListener('visibilitychange', this.handleVisibilityChange, {
      signal: this.abortController.signal,
    });
  }

  /**
   * @param {number} index
   * @param {ActivateOptions} [options]
   */
  activate(index, options = {}) {
    if (this.visibleSlides.length === 0) return;
    const clamped = Math.max(0, Math.min(this.visibleSlides.length - 1, index));
    const previous = this.state.index;
    this.state.index = clamped;
    const slide = this.visibleSlides[clamped];
    if (this.track && slide) {
      const behavior = options.animate && !this.state.reducedMotion ? 'smooth' : 'auto';
      if (behavior === 'smooth' && clamped !== previous) this.beginProgrammaticScroll(clamped);
      this.track.scrollTo({ left: slide.offsetLeft, behavior });
    }
    this.settle(clamped);
  }

  /** @param {number} index */
  beginProgrammaticScroll(index) {
    this.programmaticIndex = index;
    if (this.scrollSettleTimer) clearTimeout(this.scrollSettleTimer);
    this.scrollSettleTimer = window.setTimeout(() => {
      this.programmaticIndex = null;
      this.scrollSettleTimer = 0;
    }, this.settings.programmaticScrollMs);
  }

  /** @param {number} index */
  syncToIndex(index) {
    if (index === this.state.index) return;
    this.state.index = index;
    this.settle(index);
  }

  /** @param {number} index */
  settle(index) {
    const slide = this.visibleSlides[index] || null;
    if (slide) this.applyPoster(slide.querySelector('video[data-poster]'));
    this.state.userPausedVideo = false;
    this.syncSlideAria();
    this.renderSegmentStates(index);
    this.resetVideosExcept(slide);
    this.beginActiveProgress();
  }

  syncSlideAria() {
    this.visibleSlides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', String(index !== this.state.index));
    });
  }

  /** @param {number} index */
  renderSegmentStates(index) {
    const staticActive = this.state.reducedMotion || !this.settings.autoplay;
    this.segments.forEach((segment, position) => {
      if (!segment) return;
      segment.classList.toggle(this.classNames.segmentActive, position === index);
      const fill = this.fillOf(segment);
      if (!fill) return;
      fill.style.animation = 'none';
      if (position < index) {
        fill.style.transform = 'scaleX(1)';
      } else if (position > index) {
        fill.style.transform = 'scaleX(0)';
      } else {
        fill.style.transform = staticActive ? 'scaleX(1)' : 'scaleX(0)';
      }
    });
    this.glyphByIndex.forEach((glyph, position) => {
      glyph.classList.toggle(this.classNames.segmentActive, position === index);
      let glyphProgress = 0;
      if (position < index) glyphProgress = 1;
      else if (position === index) glyphProgress = this.settings.glyphMinProgress;
      this.setGlyphProgress(glyph, glyphProgress);
    });
  }

  /**
   * @param {HTMLElement} glyph
   * @param {number} progress
   */
  setGlyphProgress(glyph, progress) {
    const clamped = Math.max(0, Math.min(1, progress));
    glyph.style.setProperty('--kb-video-progress', String(clamped));
  }

  beginActiveProgress() {
    const slide = this.visibleSlides[this.state.index];
    if (!slide) return;
    const video = this.videoOf(slide);
    if (video) {
      try {
        video.currentTime = 0;
      } catch {
      }
      if (this.canPlayVideo()) this.playVideo(video);
      else this.markVideoPaused(slide, true);
      return;
    }
    if (!this.canRun()) return;
    const fill = this.activeFill();
    if (!fill) return;
    fill.style.animation = 'none';
    void fill.offsetWidth;
    fill.style.animation = `${this.fillAnimationName} ${this.settings.imageDurationMs}ms linear forwards`;
    fill.style.animationPlayState = 'running';
  }

  /** @param {boolean} [pauseVideo] */
  suspendProgress(pauseVideo = true) {
    const slide = this.visibleSlides[this.state.index];
    const video = slide ? this.videoOf(slide) : null;
    if (video) {
      if (pauseVideo) video.pause();
      return;
    }
    const fill = this.activeFill();
    if (fill && fill.style.animationName === this.fillAnimationName) {
      fill.style.animationPlayState = 'paused';
    }
  }

  resumeProgress() {
    const slide = this.visibleSlides[this.state.index];
    const video = slide ? this.videoOf(slide) : null;
    if (video) {
      if (this.canPlayVideo()) this.playVideo(video);
      return;
    }
    if (!this.canRun()) return;
    if (this.state.reducedMotion) return;
    const fill = this.activeFill();
    if (!fill) return;
    if (fill.style.animationName === this.fillAnimationName) {
      fill.style.animationPlayState = 'running';
    } else {
      this.beginActiveProgress();
    }
  }

  advance() {
    if (this.visibleSlides.length <= 1) return;
    const next = (this.state.index + 1) % this.visibleSlides.length;
    this.activate(next, { animate: true });
  }

  /** @returns {boolean} */
  canRun() {
    return (
      this.state.playing &&
      !document.hidden &&
      !this.state.hovered &&
      !this.state.focused &&
      !this.dragging &&
      this.visibleSlides.length > 1
    );
  }

  /** @returns {boolean} */
  canPlayVideo() {
    return (
      this.settings.videoAutoplay &&
      !this.state.reducedMotion &&
      !document.hidden &&
      !this.dragging &&
      !this.state.userPausedVideo
    );
  }

  /** @param {HTMLVideoElement} video */
  playVideo(video) {
    const promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(() => {
        const slide = video.closest(this.selectors.slide);
        if (slide instanceof HTMLElement) this.markVideoPaused(slide, true);
      });
    }
  }

  /** @param {HTMLVideoElement} video */
  toggleVideo(video) {
    if (video.paused) {
      this.state.userPausedVideo = false;
      this.playVideo(video);
    } else {
      this.state.userPausedVideo = true;
      video.pause();
    }
  }

  /**
   * @param {HTMLElement} slide
   * @param {boolean} paused
   */
  markVideoPaused(slide, paused) {
    slide.classList.toggle(this.classNames.slideVideoPaused, paused);
  }

  /** @param {HTMLElement|null} activeSlide */
  resetVideosExcept(activeSlide) {
    this.querySelectorAll(this.selectors.video).forEach((node) => {
      if (!(node instanceof HTMLVideoElement)) return;
      if (node.closest(this.selectors.slide) === activeSlide) return;
      node.pause();
      try {
        node.currentTime = 0;
      } catch {
      }
    });
  }

  handleZoom = () => {
    const slide = this.visibleSlides[this.state.index] || null;
    const media = slide ? slide.querySelector(this.selectors.media) : null;
    this.dispatchEvent(
      new CustomEvent(this.eventNames.lightboxOpen, {
        bubbles: true,
        composed: true,
        detail: { index: this.state.index, sourceElement: media },
      })
    );
  };

  /** @param {Event} event */
  handleSegmentsClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    let index = -1;
    const segment = target.closest(`.${this.classNames.segment}`);
    if (segment instanceof HTMLElement) {
      index = this.segments.indexOf(segment);
    } else {
      const glyph = target.closest(`.${this.classNames.videoIndicator}`);
      if (glyph instanceof HTMLElement) {
        this.glyphByIndex.forEach((element, position) => {
          if (element === glyph) index = position;
        });
      }
    }
    if (index < 0) return;
    this.activate(index, { animate: true });
  };

  /** @param {PointerEvent} event */
  handlePointerDown = (event) => {
    if (!this.scroller || this.dragging) return;
    if (event.button !== 0 || event.pointerType === 'touch') return;
    if (this.visibleSlides.length <= 1) return;
    this.suppressClick = false;
    event.preventDefault();

    const scroller = this.scroller;
    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startTime = performance.now();
    const controller = new AbortController();
    const { signal } = controller;
    let previous = startX;
    let velocity = 0;
    let moved = false;
    let distance = 0;
    this.dragging = true;

    /** @param {PointerEvent} moveEvent */
    const onPointerMove = (moveEvent) => {
      const current = moveEvent.clientX;
      if (!moved) {
        if (current === startX) return;
        moved = true;
        try {
          this.setPointerCapture(pointerId);
        } catch {
        }
        this.setAttribute('dragging', '');
        scroller.snap = false;
        this.suspendProgress(true);
      }
      const delta = previous - current;
      const elapsed = performance.now() - startTime;
      if (elapsed > 0) velocity = Math.round((delta / elapsed) * 1000);
      previous = current;
      distance += Math.abs(delta);
      scroller.by(delta, { instant: true });
    };

    const onPointerUp = async () => {
      controller.abort();
      this.dragging = false;
      try {
        this.releasePointerCapture(pointerId);
      } catch {
      }
      this.removeAttribute('dragging');
      if (!moved) return;
      this.suppressClick = true;
      const direction = Math.sign(velocity);
      const slow =
        Math.abs(velocity) < this.settings.dragThreshold || distance < this.settings.dragThreshold;
      const target = Math.max(
        0,
        Math.min(this.visibleSlides.length - 1, this.nearestIndex() + (slow ? 0 : direction))
      );
      const slide = this.visibleSlides[target];
      if (slide) scroller.to(slide);
      await scroller.finished;
      if (this.dragging) return;
      scroller.snap = true;
      this.resumeProgress();
    };

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  /** @param {MouseEvent} event */
  handleTrackClick = (event) => {
    if (this.suppressClick) {
      this.suppressClick = false;
      return;
    }
    const video = this.startVideoOf(event);
    if (video) this.toggleVideo(video);
  };

  /**
   * @param {MouseEvent} event
   * @returns {HTMLVideoElement|null}
   */
  startVideoOf(event) {
    const target = event.target;
    if (!(target instanceof Element)) return null;
    const slide = target.closest(this.selectors.slide);
    return slide instanceof HTMLElement ? this.videoOf(slide) : null;
  }

  /** @returns {number} */
  nearestIndex() {
    if (!this.track) return this.state.index;
    const scrollStart = this.track.scrollLeft;
    let best = 0;
    let bestDistance = Infinity;
    this.visibleSlides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - scrollStart);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }

  /** @param {Event} event */
  handleFillAnimationEnd = (event) => {
    const animationEvent = /** @type {AnimationEvent} */ (event);
    if (animationEvent.animationName !== this.fillAnimationName) return;
    if (event.target !== this.activeFill()) return;
    this.advance();
  };

  /** @param {Event} event */
  handleVideoTimeUpdate = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement) || !video.duration) return;
    if (video.closest(this.selectors.slide) !== this.visibleSlides[this.state.index]) return;
    const glyph = this.glyphByIndex.get(this.state.index);
    if (!glyph) return;
    const min = this.settings.glyphMinProgress;
    this.setGlyphProgress(glyph, min + (1 - min) * (video.currentTime / video.duration));
  };

  /** @param {Event} event */
  handleVideoEnded = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;
    if (video.closest(this.selectors.slide) !== this.visibleSlides[this.state.index]) return;
    if (!this.settings.autoplay) return;
    this.advance();
  };

  /** @param {Event} event */
  handleVideoPlayState = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;
    const slide = video.closest(this.selectors.slide);
    if (slide instanceof HTMLElement) this.markVideoPaused(slide, video.paused);
  };

  handleMouseEnter = () => {
    this.state.hovered = true;
    this.suspendProgress(false);
  };

  handleMouseLeave = () => {
    this.state.hovered = false;
    this.resumeProgress();
  };

  handleFocusIn = () => {
    this.state.focused = true;
    this.suspendProgress(false);
  };

  /** @param {FocusEvent} event */
  handleFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    this.state.focused = false;
    this.resumeProgress();
  };

  handleVisibilityChange = () => {
    if (document.hidden) this.suspendProgress(true);
    else this.resumeProgress();
  };

  /** @param {IntersectionObserverEntry[]} entries */
  handleSlideIntersect = (entries) => {
    /** @type {Element|null} */
    let best = null;
    let bestRatio = this.settings.activeThreshold;
    for (const entry of entries) {
      if (entry.isIntersecting && entry.intersectionRatio >= bestRatio) {
        bestRatio = entry.intersectionRatio;
        best = entry.target;
      }
    }
    if (!(best instanceof HTMLElement)) return;
    const index = this.visibleSlides.indexOf(best);
    if (index === -1) return;
    if (this.programmaticIndex !== null) {
      if (index !== this.programmaticIndex) return;
      this.programmaticIndex = null;
      if (this.scrollSettleTimer) {
        clearTimeout(this.scrollSettleTimer);
        this.scrollSettleTimer = 0;
      }
    }
    this.syncToIndex(index);
  };

  /** @param {Event} event */
  handleVariantChange = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail || {};
    const finish = typeof detail.finish === 'string' ? detail.finish : null;
    const shadeName = typeof detail.shadeName === 'string' ? detail.shadeName : null;
    const cartridgeProductId = detail.cartridgeProductId ?? null;
    const changed = detail.changed || null;

    if (detail.kind !== 'pen') {
      this.state.shadeName = shadeName;
      this.state.desktopShadeActive = true;
      if (!this.state.desktop) return;
      this.fadeIntoChange(() => this.applyShade(shadeName, null));
      return;
    }

    this.state.shadeName = shadeName;
    this.state.cartridgeProductId = cartridgeProductId;
    if (changed === 'shade' || changed === 'cartridgeType') {
      this.state.desktopShadeActive = true;
    } else if (changed === 'finish') {
      this.state.desktopShadeActive = false;
    }

    if (!this.state.desktop) {
      if (finish !== this.state.finish) {
        this.fadeIntoChange(() => this.applyFinish(finish, { silent: false }));
      }
      return;
    }

    if (changed === 'shade' || changed === 'cartridgeType') {
      this.fadeIntoChange(() => this.applyShade(shadeName, cartridgeProductId));
      return;
    }
    if (changed === 'finish') {
      this.fadeIntoChange(() => this.applyFinish(finish, { silent: false }));
      return;
    }
    if (this.state.mode === 'finish' && finish !== this.state.finish) {
      this.fadeIntoChange(() => this.applyFinish(finish, { silent: false }));
    }
  };

  /**
   * @param {() => void} update
   */
  fadeIntoChange(update) {
    const viewport = /** @type {HTMLElement|null} */ (this.querySelector(this.selectors.viewport));
    // Pins the outgoing frame to the framing it was shown with, so the crossfade fades
    // rather than jumps. Never cleared: crossfadeMedia drops the outgoing image later, on
    // the layer's transitionend, and clearing earlier snaps the still-visible frame back
    // to centre. With no outgoing image the position has nothing to act on.
    if (viewport) viewport.style.setProperty('--kb-media-outgoing-position', `50% ${this.mediaFocus()}`);
    crossfadeMedia(this, update);
  }

  /** @param {MediaQueryListEvent} event */
  handleBreakpointChange = (event) => {
    this.state.desktop = event.matches;
    if (this.state.desktop && this.state.desktopShadeActive) {
      this.applyShade(this.state.shadeName, this.state.cartridgeProductId);
    } else {
      this.applyFinish(this.state.finish, { silent: false });
    }
  };

  /**
   * @param {HTMLElement} slide
   * @returns {HTMLVideoElement|null}
   */
  videoOf(slide) {
    const video = slide.querySelector(this.selectors.video);
    return video instanceof HTMLVideoElement ? video : null;
  }

  /**
   * @param {HTMLElement} segment
   * @returns {HTMLElement|null}
   */
  fillOf(segment) {
    const fill = segment.querySelector(`.${this.classNames.segmentFill}`);
    return fill instanceof HTMLElement ? fill : null;
  }

  /** @returns {HTMLElement|null} */
  activeFill() {
    const segment = this.segments[this.state.index];
    return segment ? this.fillOf(segment) : null;
  }

  /**
   * @param {number|string|null|undefined} first
   * @param {number|string|null|undefined} second
   * @returns {boolean}
   */
  matchesId(first, second) {
    return first != null && second != null && String(first) === String(second);
  }
}

if (!customElements.get('kb-pdp-gallery')) {
  customElements.define('kb-pdp-gallery', KbPdpGallery);
}

export { KbPdpGallery };
